import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, delay, map, mergeMap, of, retry, tap } from 'rxjs';

import { Reconciliation, Scenario, ScenarioRun } from '@/organisations/models';
import { PollingStatus } from '@/tax/models';

import { environment } from '@env';

const LONG_DELAY_STATUS_FETCH_MS = 5000;
const invalidErrorMessage = 'Not ready';

export type WorkflowLookup = {
    workflowName: string;
    periodString: string;
};

@Injectable({
    providedIn: 'root',
})
export class ScenarioService {
    constructor(private http: HttpClient) {}

    getScenarios(organisationId: number, companyId: number): Observable<Scenario[]> {
        return this.http
            .get<any[]>(environment.endpoints.organisationCompanyScenariosList(organisationId, companyId))
            .pipe(map((scenarios) => scenarios.map((scenario: any) => new Scenario().deserialize(scenario))));
    }

    getGenericScenarios(organisationId: number): Observable<Scenario[]> {
        return this.http
            .get<any[]>(environment.endpoints.organisationGenericScenariosList(organisationId))
            .pipe(map((scenarios) => scenarios.map((scenario: any) => new Scenario().deserialize(scenario))));
    }

    getScenario(organisationId: number, companyId: number, scenarioId: number): Observable<Scenario> {
        return this.http
            .get<any>(environment.endpoints.organisationCompanyScenariosDetails(organisationId, companyId, scenarioId))
            .pipe(map((scenario) => new Scenario().deserialize(scenario)));
    }

    updateScenario(organisationId: number, companyId: number, scenario: Scenario): Observable<Scenario> {
        return this.http
            .put<any>(
                environment.endpoints.organisationCompanyScenariosDetails(organisationId, companyId, scenario.id),
                scenario.serialize(),
            )
            .pipe(
                map((updatedScenario) => {
                    return new Scenario().deserialize(updatedScenario);
                }),
            );
    }

    createScenario(organisationId: number, companyId: number, scenario: Scenario): Observable<Scenario> {
        return this.http
            .post<any>(environment.endpoints.organisationCompanyScenariosList(organisationId, companyId), scenario.serialize())
            .pipe(map((createdScenario) => new Scenario().deserialize(createdScenario)));
    }

    deleteScenario(organisationId: number, companyId: number, scenarioId: number): Observable<any> {
        return this.http.delete(environment.endpoints.organisationCompanyScenariosDetails(organisationId, companyId, scenarioId));
    }

    getScenarioRuns(organisationId: number, companyId: number, scenarioId: number): Observable<ScenarioRun[]> {
        return this.http
            .get<any[]>(environment.endpoints.organisationCompanyScenariosScenarioRunsList(organisationId, companyId, scenarioId))
            .pipe(map((scenarioRuns) => scenarioRuns.map((scenarioRun: any) => new ScenarioRun().deserialize(scenarioRun))));
    }

    getScenarioRun(organisationId: number, companyId: number, scenarioId: number, runId: number): Observable<ScenarioRun> {
        return this.http
            .get<any[]>(environment.endpoints.organisationCompanyScenariosScenarioRunsDetails(organisationId, companyId, scenarioId, runId))
            .pipe(map((scenarioRun) => new ScenarioRun().deserialize(scenarioRun)));
    }

    createScenarioRun(organisationId: number, companyId: number, scenarioId: number, run: ScenarioRun): Observable<ScenarioRun> {
        return this.http
            .post<any>(
                environment.endpoints.organisationCompanyScenariosScenarioRunsList(organisationId, companyId, scenarioId),
                run.serialize(),
            )
            .pipe(map((createdScenarioRun) => new ScenarioRun().deserialize(createdScenarioRun)));
    }

    createScenarioRunAndPollResult(
        organisationId: number,
        companyId: number,
        scenarioId: number,
        run: ScenarioRun,
    ): Observable<ScenarioRun> {
        return this.createScenarioRun(organisationId, companyId, scenarioId, run).pipe(
            delay(LONG_DELAY_STATUS_FETCH_MS),
            mergeMap((newRun) =>
                this.getScenarioRun(organisationId, companyId, scenarioId, newRun.id).pipe(
                    tap((scenarioRun) => {
                        if (scenarioRun.status === PollingStatus.Init || scenarioRun.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(LONG_DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    companyWorkflowLookup(organisationId: number, companyId: number, periodId: number, workflowId: number): Observable<WorkflowLookup> {
        return this.http
            .post<any>(environment.endpoints.organisationCompanyWorkflowLookup(organisationId, companyId), {
                'period_id': periodId,
                'workflow_id': workflowId,
            })
            .pipe(
                map((response) => {
                    return { workflowName: response.workflow_name, periodString: response.period_string } as WorkflowLookup;
                }),
            );
    }

    getScenarioContent(organisationId: number, companyId: number, scenarioId: number): Observable<Reconciliation[]> {
        return this.http
            .get<any[]>(environment.endpoints.organisationCompaniesScenariosContent(organisationId, companyId, scenarioId))
            .pipe(map((reconciliations) => reconciliations.map((reconciliation: any) => new Reconciliation().deserialize(reconciliation))));
    }
}
